/* ================================================================
   Solve layer - calculateTool / basicMethodTool equivalent.
   Takes a ProjectModel, runs constraint solving, returns resolved
   board/frame geometry arrays for each side.

   Features:
   - Per-layer memoization keyed by a canonical content hash.
   - Unchanged layers return the SAME object reference on re-solve,
     so downstream (renderNodes, BoardMesh memo) can skip work.
   ================================================================ */

import type {
  ProjectModel, Layer, SideId, Dimensions3, Vec3, SideSpanOverride, ElementOverride,
} from '../model/types';

// --------------- Geometry helpers ---------------

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function getSideThicknessTotals(project: ProjectModel): Record<SideId, number> {
  const totals: Record<SideId, number> = {
    pedestal: 0,
    topCap: 0,
    sideFront: 0,
    sideBack: 0,
    endLeft: 0,
    endRight: 0,
  };

  for (const side of project.sides) {
    if (!side.enabled) continue;
    totals[side.sideId] = side.layers.reduce((sum, layer) => sum + layer.thickness, 0);
  }

  return totals;
}

/**
 * 每面的 2D 展开尺寸 (u, v)。
 * 与旧系统一致：所有面使用完整外尺寸，拐角处自然重叠（和真实木箱构造相同）。
 */
function sideSpan(
  sideId: SideId,
  inner: Dimensions3,
  outer: Dimensions3,
  thicknessTotals: Record<SideId, number>,
  sideLinkageMode: 'linked' | 'independent',
  sideSpanOverrides?: Partial<Record<SideId, SideSpanOverride>>,
): { spanU: number; spanV: number } {
  const { length: L, width: W, height: H } = outer;
  const bottom = thicknessTotals.pedestal;
  const top = thicknessTotals.topCap;
  const sideHeight = Math.max(0, H - bottom - top);
  const override = sideLinkageMode === 'independent' ? sideSpanOverrides?.[sideId] : undefined;
  if (override) {
    return { spanU: override.spanU, spanV: override.spanV };
  }
  const spanLength = sideLinkageMode === 'linked' ? L : inner.length;
  const spanWidth = sideLinkageMode === 'linked' ? W : inner.width;
  switch (sideId) {
    case 'pedestal':
    case 'topCap':
      return { spanU: L, spanV: W };
    case 'sideFront':
    case 'sideBack':
      return { spanU: spanLength, spanV: sideHeight };
    case 'endLeft':
    case 'endRight':
      return { spanU: spanWidth, spanV: sideHeight };
  }
}

/**
 * Map a 2D local coordinate (u, v) on a side face + layer depth
 * into 3D world coordinates (x, y, z).
 */
function mapToWorld(
  sideId: SideId,
  u: number, v: number, depth: number,
  inner: Dimensions3,
  outer: Dimensions3,
  thicknessTotals: Record<SideId, number>,
  sideLinkageMode: 'linked' | 'independent',
  sideSpanOverrides?: Partial<Record<SideId, SideSpanOverride>>,
): Vec3 {
  const { length: L, width: W, height: H } = outer;
  const bottom = thicknessTotals.pedestal;
  const front = thicknessTotals.sideFront;
  const left = thicknessTotals.endLeft;
  const pedestalOverride = sideSpanOverrides?.pedestal;
  const topCapOverride = sideSpanOverrides?.topCap;
  const frontOverride = sideSpanOverrides?.sideFront;
  const backOverride = sideSpanOverrides?.sideBack;
  const leftOverride = sideSpanOverrides?.endLeft;
  const rightOverride = sideSpanOverrides?.endRight;
  const independentLengthOffset = sideLinkageMode === 'independent'
    ? (frontOverride?.offsetU ?? backOverride?.offsetU ?? left)
    : 0;
  const independentWidthOffset = sideLinkageMode === 'independent'
    ? (leftOverride?.offsetU ?? rightOverride?.offsetU ?? front)
    : 0;
  switch (sideId) {
    case 'pedestal':
      return {
        x: (sideLinkageMode === 'independent' ? (pedestalOverride?.offsetU ?? 0) : 0) + u,
        y: depth,
        z: (sideLinkageMode === 'independent' ? (pedestalOverride?.offsetV ?? 0) : 0) + v,
      };
    case 'topCap':
      return {
        x: (sideLinkageMode === 'independent' ? (topCapOverride?.offsetU ?? 0) : 0) + u,
        y: H - depth,
        z: (sideLinkageMode === 'independent' ? (topCapOverride?.offsetV ?? 0) : 0) + v,
      };
    case 'sideFront': return { x: independentLengthOffset + u, y: bottom + v, z: depth };
    case 'sideBack':  return { x: independentLengthOffset + u, y: bottom + v, z: W - depth };
    case 'endLeft':   return { x: depth, y: bottom + v, z: independentWidthOffset + u };
    case 'endRight':  return { x: L - depth, y: bottom + v, z: independentWidthOffset + u };
  }
}

function mapSizeToWorld(
  sideId: SideId,
  sizeU: number, sizeV: number, sizeDepth: number,
): { geoW: number; geoH: number; geoD: number } {
  switch (sideId) {
    case 'pedestal':
    case 'topCap':
      return { geoW: sizeU, geoH: sizeDepth, geoD: sizeV };
    case 'sideFront':
    case 'sideBack':
      return { geoW: sizeU, geoH: sizeV, geoD: sizeDepth };
    case 'endLeft':
    case 'endRight':
      return { geoW: sizeDepth, geoH: sizeV, geoD: sizeU };
  }
}

// --------------- Board / frame helpers ---------------

interface CalculatedBoard {
  width: number;
  thickness: number;
  length: number;
}

function calculateBoardDimensions(
  layer: Layer,
  spanU: number,
  spanV: number,
): CalculatedBoard {
  const ss = layer.styleSetting;
  const bb = layer.blockBoard;
  const isHor = ss.directions.isHor === 1;
  const autoBoardLength = isHor ? spanU : spanV;
  const boardSpan = isHor ? spanV : spanU;
  const boardLength = bb.boardLength > 0 ? bb.boardLength : autoBoardLength;

  let width: number;
  if (bb.boardWidth > 0) {
    width = bb.boardWidth;
  } else if (layer.style === 1) {
    width = boardSpan;
  } else if (ss.logics.size.board === 1) {
    const count = ss.count.board || 3;
    const gapMm = 15;
    const totalGap = (count - 1) * gapMm;
    width = Math.max(20, Math.floor((boardSpan - totalGap) / count));
  } else {
    width = clamp(90, 10, boardSpan);
  }

  return { width, thickness: layer.thickness, length: boardLength };
}

function calculateBoardCount(
  layer: Layer,
  boardSpan: number,
  boardWidth: number,
): number {
  if (layer.style === 1) return 1;
  const ss = layer.styleSetting;
  if (ss.count.board > 0) return ss.count.board;
  const gapMm = 15;
  if (boardWidth <= 0) return 1;
  return Math.max(1, Math.floor((boardSpan + gapMm) / (boardWidth + gapMm)));
}

function calculateBoardSpacing(
  count: number,
  boardSpan: number,
  boardWidth: number,
): number {
  if (count <= 1) return 0;
  const totalBoardWidth = count * boardWidth;
  return Math.max(0, (boardSpan - totalBoardWidth) / (count - 1));
}

function calculateFramePositions(
  count: number,
  span: number,
  frameWidth: number,
): number[] {
  if (count <= 0) return [];
  const positions: number[] = [];
  if (count === 1) {
    positions.push(span / 2 - frameWidth / 2);
  } else {
    const gap = (span - count * frameWidth) / (count - 1);
    for (let i = 0; i < count; i++) {
      positions.push(i * (frameWidth + gap));
    }
  }
  return positions;
}

function calculateElementPositions(
  count: number,
  span: number,
  size: number,
): number[] {
  if (count <= 0) return [];
  if (count === 1) return [span / 2 - size / 2];

  const totalSize = count * size;
  const gap = (span - totalSize) / (count - 1);
  const positions: number[] = [];
  for (let i = 0; i < count; i++) {
    positions.push(i * (size + gap));
  }
  return positions;
}

/**
 * 等分间距排布：N 根构件把 span 分成 N+1 等份间距。
 * 返回每根构件的中心坐标（相对于 span 起点）。
 */
function calculateEvenSpaceCenters(
  count: number,
  span: number,
): number[] {
  if (count <= 0) return [];
  const centers: number[] = [];
  for (let i = 0; i < count; i++) {
    centers.push(span * (i + 1) / (count + 1));
  }
  return centers;
}

// --------------- Solve types ---------------

export interface SolvedElement {
  position: Vec3;
  geoW: number;
  geoH: number;
  geoD: number;
}

export interface SolvedLayerLayout {
  layerIndex: number;
  boards: SolvedElement[];
  frames: SolvedElement[][];
  horBraces: SolvedElement[][];
}

export interface SolvedSideLayout {
  sideId: SideId;
  sideIndex: number;
  spanU: number;
  spanV: number;
  layers: SolvedLayerLayout[];
}

// --------------- Memoization ---------------

/**
 * Canonical hash capturing every input that affects solveLayer output.
 * Fast string concatenation — no JSON.stringify overhead.
 */
function layerCacheKey(
  sideId: SideId,
  outer: Dimensions3,
  spanU: number,
  spanV: number,
  depthOffset: number,
  layer: Layer,
  bottomThickness: number,
  sideLinkageMode: 'linked' | 'independent',
  sideSpanOverrideKey: string,
): string {
  const ss = layer.styleSetting;
  const bb = layer.blockBoard;
  return (
    sideId + '|' +
    outer.length + ',' + outer.width + ',' + outer.height + '|' +
    spanU + ',' + spanV + '|' +
    bottomThickness + '|' +
    sideLinkageMode + '|' +
    sideSpanOverrideKey + '|' +
    depthOffset + '|' +
    layer.thickness + '|' +
    layer.style + '|' +
    layer.direction + '|' +
    ss.directions.isHor + '|' +
    ss.count.board + '|' +
    ss.count.frame.join(',') + '|' +
    ss.count.horBrace.join(',') + '|' +
    ss.logics.size.board + '|' +
    bb.boardLength + ',' + bb.boardWidth + ',' + bb.count + ',' + (bb.centerSpacing ?? 0) + '|' +
    ss.count.horBrace.join(',') + '|' +
    (layer.elementOverrides ? JSON.stringify(layer.elementOverrides) : '')
  );
}

const LAYER_CACHE_LIMIT = 512;
const layerCache = new Map<string, SolvedLayerLayout>();

function cacheGet(key: string): SolvedLayerLayout | undefined {
  const hit = layerCache.get(key);
  if (hit) {
    // LRU touch
    layerCache.delete(key);
    layerCache.set(key, hit);
  }
  return hit;
}

function cachePut(key: string, value: SolvedLayerLayout): void {
  if (layerCache.size >= LAYER_CACHE_LIMIT) {
    const oldest = layerCache.keys().next().value;
    if (oldest) layerCache.delete(oldest);
  }
  layerCache.set(key, value);
}

export function clearSolveCache(): void {
  layerCache.clear();
}

// --------------- Main solve entry point ---------------

export function solveProject(project: ProjectModel): SolvedSideLayout[] {
  const { outerSize } = project;
  const sideLinkageMode = project.sideLinkageMode ?? 'linked';
  const thicknessTotals = getSideThicknessTotals(project);
  const sideSpanOverrides = project.sideSpanOverrides;
  const results: SolvedSideLayout[] = [];

  for (let si = 0; si < project.sides.length; si++) {
    const side = project.sides[si];
    if (!side.enabled) continue;

    const { spanU, spanV } = sideSpan(
      side.sideId,
      project.innerSize,
      outerSize,
      thicknessTotals,
      sideLinkageMode,
      sideSpanOverrides,
    );
    const layerLayouts: SolvedLayerLayout[] = new Array(side.layers.length);
    let depthAccum = 0;

    for (let li = 0; li < side.layers.length; li++) {
      const layer = side.layers[li];
      const key = layerCacheKey(
        side.sideId,
        outerSize,
        spanU,
        spanV,
        depthAccum,
        layer,
        thicknessTotals.pedestal,
        sideLinkageMode,
        JSON.stringify(sideSpanOverrides?.[side.sideId] ?? null),
      );
      const cached = cacheGet(key);
      let layout: SolvedLayerLayout;
      if (cached) {
        layout = cached;
      } else {
        layout = solveLayer(
          layer,
          side.sideId,
          spanU,
          spanV,
          depthAccum,
          project.innerSize,
          outerSize,
          thicknessTotals,
          sideLinkageMode,
          sideSpanOverrides,
        );
        layout.layerIndex = li;
        cachePut(key, layout);
      }
      // Ensure layerIndex matches current position (cache key doesn't include it)
      if (layout.layerIndex !== li) {
        layout = { ...layout, layerIndex: li };
      }
      layerLayouts[li] = layout;
      depthAccum += layer.thickness;
    }

    results.push({
      sideId: side.sideId,
      sideIndex: si,
      spanU,
      spanV,
      layers: layerLayouts,
    });
  }

  return results;
}

function solveLayer(
  layer: Layer,
  sideId: SideId,
  spanU: number,
  spanV: number,
  depthOffset: number,
  innerSize: Dimensions3,
  outerSize: Dimensions3,
  thicknessTotals: Record<SideId, number>,
  sideLinkageMode: 'linked' | 'independent',
  sideSpanOverrides?: Partial<Record<SideId, SideSpanOverride>>,
): SolvedLayerLayout {
  const result: SolvedLayerLayout = {
    layerIndex: 0,
    boards: [],
    frames: [],
    horBraces: [],
  };

  const thick = layer.thickness;
  const depthCenter = depthOffset + thick / 2;

  // ---- 板条 (style=0 普通, style=4 底部粗板条) ----
  if (layer.style === 4) {
    const xCount = layer.styleSetting.count.frame[0] || 3;
    const zCount = layer.styleSetting.count.frame[1] || 3;
    const blockLength = layer.blockBoard.boardLength > 0 ? layer.blockBoard.boardLength : 90;
    const blockWidth = layer.blockBoard.boardWidth > 0 ? layer.blockBoard.boardWidth : 90;
    const xPositions = calculateElementPositions(xCount, spanU, blockLength);
    const zPositions = calculateElementPositions(zCount, spanV, blockWidth);

    for (let zi = 0; zi < zCount; zi++) {
      const row: SolvedElement[] = [];
      for (let xi = 0; xi < xCount; xi++) {
        const worldPos = mapToWorld(
          sideId,
          xPositions[xi] + blockLength / 2,
          zPositions[zi] + blockWidth / 2,
          depthCenter,
          innerSize,
          outerSize,
          thicknessTotals,
          sideLinkageMode,
          sideSpanOverrides,
        );
        row.push({
          position: worldPos,
          ...mapSizeToWorld(sideId, blockLength, blockWidth, thick),
        });
      }
      result.frames.push(row);
    }

    return result;
  }

  // ---- 田字 (style=3: 边框 + 内撑 + 横撑，参考老代码 NewPeri + PerimeterFunction) ----
  if (layer.style === 3) {
    const ss = layer.styleSetting;
    const isHor = ss.directions.isHor === 1;
    const boardCount = ss.count.board || 0;  // 内撑数量（沿主方向）
    const fw = Math.max(20, Math.min(90, Math.floor(Math.min(spanU, spanV) / 8)));

    // 主轴/副轴：isHor=0 时主轴=U（内撑沿U排布，横撑沿V），isHor=1 时反过来
    const mainSpan = isHor ? spanV : spanU;  // 内撑排布方向的总跨度
    const crossSpan = isHor ? spanU : spanV; // 横撑排布方向的总跨度

    // ---- 1. 边框 (frame) ----
    // 主方向 2 根边框：全长 crossSpan，贴主轴两端
    const mainEdgeGroup: SolvedElement[] = [];
    for (const pos of [fw / 2, mainSpan - fw / 2]) {
      const uArg = isHor ? crossSpan / 2 : pos;
      const vArg = isHor ? pos : crossSpan / 2;
      const wp = mapToWorld(sideId, uArg, vArg, depthCenter,
        innerSize, outerSize, thicknessTotals, sideLinkageMode, sideSpanOverrides);
      const geo = isHor
        ? mapSizeToWorld(sideId, crossSpan, fw, thick)
        : mapSizeToWorld(sideId, fw, crossSpan, thick);
      mainEdgeGroup.push({ position: wp, ...geo });
    }
    result.frames.push(mainEdgeGroup);

    // 副方向 2 根边框：长度 = mainSpan - 2*fw（扣除主边框），贴副轴两端
    const crossLen = mainSpan - 2 * fw;
    const crossEdgeGroup: SolvedElement[] = [];
    for (const pos of [fw / 2, crossSpan - fw / 2]) {
      const wp = mapToWorld(sideId, isHor ? pos : mainSpan / 2, isHor ? mainSpan / 2 : pos, depthCenter,
        innerSize, outerSize, thicknessTotals, sideLinkageMode, sideSpanOverrides);
      const geo = isHor
        ? mapSizeToWorld(sideId, fw, crossLen, thick)
        : mapSizeToWorld(sideId, crossLen, fw, thick);
      crossEdgeGroup.push({ position: wp, ...geo });
    }
    result.frames.push(crossEdgeGroup);

    // ---- 2. 内撑 (board): 在两主边框之间等分间距排布 ----
    // 内撑沿副轴延伸，长度 = crossSpan - 2*fw（扣除两端副边框宽度）
    // 内撑位置：在 innerMain（两主边框内侧净距）内等分间距
    const innerMain = mainSpan - 2 * fw;
    const boardLength = crossSpan - 2 * fw; // 内撑长度：两副边框内侧净距
    const boardCentersInInner = calculateEvenSpaceCenters(boardCount, innerMain);
    for (let i = 0; i < boardCentersInInner.length; i++) {
      const mainPos = fw + boardCentersInInner[i]; // 偏移一个主边框宽度
      const wp = mapToWorld(sideId,
        isHor ? crossSpan / 2 : mainPos,
        isHor ? mainPos : crossSpan / 2,
        depthCenter, innerSize, outerSize, thicknessTotals, sideLinkageMode, sideSpanOverrides);
      const geo = isHor
        ? mapSizeToWorld(sideId, boardLength, fw, thick)
        : mapSizeToWorld(sideId, fw, boardLength, thick);
      result.boards.push({ position: wp, ...geo });
    }

    // ---- 3. 横撑 (horBrace): 在每个格内等分间距排布 ----
    // 收集主轴方向所有构件的中心位置（主边框 + 内撑）
    const mainMembers: Array<{ center: number; w: number }> = [];
    mainMembers.push({ center: fw / 2, w: fw }); // 主边框左/下
    for (const c of boardCentersInInner) {
      mainMembers.push({ center: fw + c, w: fw });
    }
    mainMembers.push({ center: mainSpan - fw / 2, w: fw }); // 主边框右/上

    const innerCross = crossSpan - 2 * fw; // 两副边框内侧之间的净距

    for (let col = 0; col < mainMembers.length - 1; col++) {
      const braceCount = ss.count.horBrace[col] || ss.count.horBrace[0] || 0;
      if (braceCount <= 0) { result.horBraces.push([]); continue; }

      const left = mainMembers[col];
      const right = mainMembers[col + 1];
      const braceLen = right.center - left.center - (left.w + right.w) / 2;
      const braceCenterMain = (left.center + right.center) / 2;

      const braceCentersInInner = calculateEvenSpaceCenters(braceCount, innerCross);
      const group: SolvedElement[] = [];
      for (let i = 0; i < braceCentersInInner.length; i++) {
        const crossPos = fw + braceCentersInInner[i]; // 偏移一个副边框宽度
        const wp = mapToWorld(sideId,
          isHor ? crossPos : braceCenterMain,
          isHor ? braceCenterMain : crossPos,
          depthCenter, innerSize, outerSize, thicknessTotals, sideLinkageMode, sideSpanOverrides);
        const geo = isHor
          ? mapSizeToWorld(sideId, fw, braceLen, thick)
          : mapSizeToWorld(sideId, braceLen, fw, thick);
        group.push({ position: wp, ...geo });
      }
      result.horBraces.push(group);
    }

    return result;
  }

  if (layer.style === 0) {
    const bb = layer.blockBoard;
    const overrides = layer.elementOverrides;
    const runsAlongLength = layer.styleSetting.directions.isHor === 1;
    const boardSpan = runsAlongLength ? spanV : spanU;

    const count = (layer.styleSetting.count.board > 0) ? layer.styleSetting.count.board
      : (bb.count > 0 ? bb.count : 3);

    const defaultW = (bb.boardWidth > 0) ? bb.boardWidth
      : Math.max(20, Math.floor((boardSpan - (count - 1) * 15) / count));

    const autoLength = runsAlongLength ? spanU : spanV;
    const defaultLen = (bb.boardLength > 0) ? bb.boardLength : autoLength;

    // 构建每根板条的实际宽度数组（考虑单根覆盖）
    const widths: number[] = [];
    for (let i = 0; i < count; i++) {
      const ov = overrides?.find(o => o.index === i);
      widths.push(ov?.width ?? defaultW);
    }

    // 按实际宽度数组计算间距和位置
    const totalWidth = widths.reduce((s, w) => s + w, 0);
    const gap = count > 1 ? Math.max(0, (boardSpan - totalWidth) / (count - 1)) : 0;

    let cursor = 0;
    for (let i = 0; i < count; i++) {
      const ov = overrides?.find(o => o.index === i);
      const curW = widths[i];
      const curLen = ov?.length ?? defaultLen;
      const curThick = ov?.thickness ?? thick;

      const center = cursor + curW / 2;
      const uCenter = runsAlongLength ? spanU / 2 : center;
      const vCenter = runsAlongLength ? center : spanV / 2;

      const worldPos = mapToWorld(
        sideId,
        uCenter,
        vCenter,
        depthOffset + curThick / 2,
        innerSize,
        outerSize,
        thicknessTotals,
        sideLinkageMode,
        sideSpanOverrides,
      );
      const geoSize = runsAlongLength
        ? mapSizeToWorld(sideId, curLen, curW, curThick)
        : mapSizeToWorld(sideId, curW, curLen, curThick);

      result.boards.push({ position: worldPos, ...geoSize });
      cursor += curW + gap;
    }
  } else {
    // style=1 整板 / style=2 框架 等的板子
    const isHor = layer.styleSetting.directions.isHor === 1;
    const boardDim = calculateBoardDimensions(layer, spanU, spanV);
    const boardSpan = isHor ? spanV : spanU;
    const boardCount = calculateBoardCount(layer, boardSpan, boardDim.width);
    const boardSpacing = calculateBoardSpacing(boardCount, boardSpan, boardDim.width);

    for (let i = 0; i < boardCount; i++) {
      const offset = i * (boardDim.width + boardSpacing);
      const uCenter = isHor ? spanU / 2 : offset + boardDim.width / 2;
      const vCenter = isHor ? offset + boardDim.width / 2 : spanV / 2;

      const worldPos = mapToWorld(
        sideId,
        uCenter,
        vCenter,
        depthCenter,
        innerSize,
        outerSize,
        thicknessTotals,
        sideLinkageMode,
        sideSpanOverrides,
      );
      const geoSize = isHor
        ? mapSizeToWorld(sideId, boardDim.length, boardDim.width, thick)
        : mapSizeToWorld(sideId, boardDim.width, boardDim.length, thick);

      result.boards.push({ position: worldPos, ...geoSize });
    }
  }

  // ---- Frames ----
  const ss = layer.styleSetting;
  const frameCounts = ss.count.frame;
  for (let axis = 0; axis < frameCounts.length; axis++) {
    const count = frameCounts[axis] || 0;
    if (count <= 0) { result.frames.push([]); continue; }

    const frameSpan = axis === 0 ? spanU : spanV;
    const frameLength = axis === 0 ? spanV : spanU;
    const frameWidth = Math.max(20, Math.min(90, Math.floor(frameSpan / (count * 2))));
    const positions = calculateFramePositions(count, frameSpan, frameWidth);

    const group: SolvedElement[] = [];
    for (const pos of positions) {
      const uCenter = axis === 0 ? pos + frameWidth / 2 : spanU / 2;
      const vCenter = axis === 0 ? spanV / 2 : pos + frameWidth / 2;

      const frameDepthCenter = depthOffset + thick + thick / 2;
      const worldPos = mapToWorld(
        sideId,
        uCenter,
        vCenter,
        frameDepthCenter,
        innerSize,
        outerSize,
        thicknessTotals,
        sideLinkageMode,
        sideSpanOverrides,
      );
      const geoSize = axis === 0
        ? mapSizeToWorld(sideId, frameWidth, frameLength, thick)
        : mapSizeToWorld(sideId, frameLength, frameWidth, thick);

      group.push({ position: worldPos, ...geoSize });
    }
    result.frames.push(group);
  }

  // ---- Horizontal braces ----
  const braceCounts = ss.count.horBrace;
  for (let g = 0; g < braceCounts.length; g++) {
    const count = braceCounts[g] || 0;
    if (count <= 0) { result.horBraces.push([]); continue; }

    const braceWidth = Math.max(20, Math.min(80, thick));
    const positions = calculateFramePositions(count, spanU, braceWidth);

    const group: SolvedElement[] = [];
    for (const pos of positions) {
      const uCenter = pos + braceWidth / 2;
      const vCenter = spanV / 2;
      const braceDepthCenter = depthOffset + thick + thick / 2;
      const worldPos = mapToWorld(
        sideId,
        uCenter,
        vCenter,
        braceDepthCenter,
        innerSize,
        outerSize,
        thicknessTotals,
        sideLinkageMode,
        sideSpanOverrides,
      );
      const geoSize = mapSizeToWorld(sideId, braceWidth, spanV, thick);
      group.push({ position: worldPos, ...geoSize });
    }
    result.horBraces.push(group);
  }

  return result;
}

/**
 * Validate outer size from inner + thicknesses.
 */
export function validateOuterSize(
  inner: Dimensions3,
  thicknessMap: Map<SideId, number>,
): Dimensions3 {
  const front = thicknessMap.get('sideFront') ?? 18;
  const back = thicknessMap.get('sideBack') ?? 18;
  const left = thicknessMap.get('endLeft') ?? 18;
  const right = thicknessMap.get('endRight') ?? 18;
  const bottom = thicknessMap.get('pedestal') ?? 18;
  const top = thicknessMap.get('topCap') ?? 18;
  return {
    length: inner.length + left + right,
    width: inner.width + front + back,
    height: inner.height + bottom + top,
  };
}
